/**
 * The on-screen keyboard (Settings > Keyboard hints).
 *
 * Every key is tinted by the finger that types it, so the colour zones teach
 * the finger map at a glance, and the next key to press lights up with its
 * finger named underneath. Off by default; fades back when the animal is
 * behind it, like the word list.
 */
import Phaser from 'phaser';
import { fingerFor, fingerLabel, KEY_ROWS } from '../../systems/keyboardHints';
import type { Finger, Hand, KeyHint } from '../../systems/keyboardHints';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH } from '../../types';

/** Over the platforms and word list, under the animal's word panel. */
export const KEYBOARD_HINT_DEPTH = 24;

const KEY = 28;
const GAP = 4;
const PITCH = KEY + GAP;
const PAD = 10;

/** One colour per finger, the same on both hands. */
export const FINGER_COLORS: Readonly<Record<Finger, number>> = {
  pinky: 0xc08bff,
  ring: 0x4ea8de,
  middle: 0x2ec4b6,
  index: 0xffc145,
  thumb: 0xb8c6d9,
};

interface KeyCap {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly color: number;
}

const FADED_ALPHA = 0.25;

export class KeyboardHint {
  private readonly container: Phaser.GameObjects.Container;
  private readonly caps: KeyCap[] = [];
  private readonly lit: Phaser.GameObjects.Graphics;
  private readonly label: Phaser.GameObjects.Text;
  private readonly left: number;
  private readonly top: number;
  private readonly width: number;
  private readonly height: number;
  private shown = '';
  private faded = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly reducedMotion: boolean,
  ) {
    // Rows are staggered like a real keyboard; extra keys on the ends.
    const rowsWidth = 11.6 * PITCH;
    this.width = rowsWidth + PAD * 2;
    this.height = 4 * PITCH + PAD * 2 - GAP;
    this.left = Math.round(GAME_WIDTH / 2 - this.width / 2);
    this.top = GAME_HEIGHT - this.height - 8;

    const base = scene.add.graphics();
    base.fillStyle(0x0b1220, 0.62);
    base.fillRoundedRect(this.left, this.top, this.width, this.height, 14);

    const texts: Phaser.GameObjects.Text[] = [];
    const addCap = (
      id: string,
      x: number,
      y: number,
      w: number,
      color: number,
      text: string,
    ): void => {
      this.caps.push({ id, x, y, w, color });
      base.fillStyle(color, 0.3);
      base.fillRoundedRect(x, y, w, KEY, 6);
      base.lineStyle(1.5, color, 0.7);
      base.strokeRoundedRect(x, y, w, KEY, 6);
      texts.push(
        scene.add
          .text(x + w / 2, y + KEY / 2, text, {
            fontFamily: FONT_FAMILY,
            fontSize: text.length > 1 ? '11px' : '15px',
            color: '#f7fbff',
          })
          .setOrigin(0.5),
      );
    };

    const x0 = this.left + PAD;
    const y0 = this.top + PAD;
    const stagger = [0, 0.3, 0.75];
    KEY_ROWS.forEach((row, r) => {
      const y = y0 + r * PITCH;
      let x = x0 + (stagger[r] ?? 0) * PITCH;
      if (r === 2) {
        addCap('ShiftLeft', x0, y, PITCH * 0.75 - GAP, FINGER_COLORS.pinky, '⇧');
      }
      for (const key of row) {
        const finger = fingerFor(key);
        addCap(
          key,
          x,
          y,
          KEY,
          finger === null ? 0x7e8fa6 : FINGER_COLORS[finger.finger],
          key.toUpperCase(),
        );
        x += PITCH;
      }
      if (r === 0) addCap('Backspace', x, y, x0 + 11.6 * PITCH - GAP - x, FINGER_COLORS.pinky, '⌫');
      if (r === 1) addCap('Enter', x, y, x0 + 11.6 * PITCH - GAP - x, FINGER_COLORS.pinky, 'Enter');
      if (r === 2)
        addCap('ShiftRight', x, y, x0 + 11.6 * PITCH - GAP - x, FINGER_COLORS.pinky, '⇧');
    });
    const spaceY = y0 + 3 * PITCH;
    addCap('Space', x0 + 3.2 * PITCH, spaceY, 5.2 * PITCH, FINGER_COLORS.thumb, 'space');

    this.lit = scene.add.graphics();
    this.label = scene.add
      .text(x0 + 1.5 * PITCH, spaceY + KEY / 2, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    this.container = scene.add.container(0, 0, [base, this.lit, ...texts, this.label]);
    this.container.setScrollFactor(0).setDepth(KEYBOARD_HINT_DEPTH);

    if (!reducedMotion) {
      scene.tweens.add({
        targets: this.lit,
        alpha: 0.55,
        duration: 480,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /** Light up the key (and Shift) to press next. Cheap to call every frame. */
  show(hint: KeyHint | null): void {
    const signature = hint === null ? '' : `${hint.key}|${hint.shift ?? ''}`;
    if (signature === this.shown) return;
    this.shown = signature;

    this.lit.clear();
    if (hint === null) {
      this.label.setText('');
      return;
    }
    const color = FINGER_COLORS[hint.finger.finger];
    this.light(hint.key, color);
    if (hint.shift !== null) this.light(shiftId(hint.shift), FINGER_COLORS.pinky);
    this.label.setText(fingerLabel(hint.finger)).setColor(hex(color));
  }

  /** Screen-space box, so Game can tell when the animal is behind it. */
  get bounds(): Phaser.Geom.Rectangle {
    return new Phaser.Geom.Rectangle(this.left, this.top, this.width, this.height);
  }

  setFaded(faded: boolean): void {
    if (faded === this.faded) return;
    this.faded = faded;
    const alpha = faded ? FADED_ALPHA : 1;
    this.scene.tweens.killTweensOf(this.container);
    if (this.reducedMotion) {
      this.container.setAlpha(alpha);
      return;
    }
    this.scene.tweens.add({ targets: this.container, alpha, duration: 160, ease: 'Sine.easeOut' });
  }

  destroy(): void {
    this.scene.tweens.killTweensOf(this.lit);
    this.container.destroy(true);
  }

  private light(id: string, color: number): void {
    const cap = this.caps.find((c) => c.id === id);
    if (cap === undefined) return;
    this.lit.fillStyle(color, 1);
    this.lit.fillRoundedRect(cap.x, cap.y, cap.w, KEY, 6);
    this.lit.lineStyle(3, 0xffffff, 1);
    this.lit.strokeRoundedRect(cap.x - 2, cap.y - 2, cap.w + 4, KEY + 4, 8);
  }
}

function shiftId(hand: Hand): string {
  return hand === 'left' ? 'ShiftLeft' : 'ShiftRight';
}

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}
